import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { isAdminAuthenticated, getAdmin, clearAdminAuth } from '../utils/adminData'
import LoadingSpinner from '../components/LoadingSpinner'
import { useAdminData } from '../components/admin/hooks/useAdminData'
import AdminHeader from '../components/admin/AdminHeader'
import AdminTabNav from '../components/admin/AdminTabNav'
import StatsTab from '../components/admin/tabs/StatsTab'
import FeedbackTab from '../components/admin/tabs/FeedbackTab'
import Build3Tab from '../components/admin/tabs/Build3Tab'
import WellnessTab from '../components/admin/tabs/WellnessTab'
import CohortTab from '../components/admin/tabs/CohortTab'
import MidDayResetTab from '../components/admin/tabs/MidDayResetTab'
import SpacesTab from '../components/admin/tabs/SpacesTab'
import EventsTab from '../components/admin/tabs/EventsTab'
import OneOnOneTab from '../components/admin/tabs/OneOnOneTab'
import ConsultationsTab from '../components/admin/tabs/ConsultationsTab'
import FacilitatorConsultationsTab from '../components/admin/tabs/FacilitatorConsultationsTab'
import CertificationsTab from '../components/admin/tabs/CertificationsTab'

const AdminDashboard = () => {
  const navigate = useNavigate()
  const [admin, setAdmin] = useState(null)
  const [activeTab, setActiveTab] = useState('stats')

  const {
    data,
    loadingTab,
    tabError,
    exportingCSV,
    loadTabData,
    fetchStats,
    fetchSpaces,
    fetchEvents,
    fetchConsultations,
    fetchFacConsultations,
    fetchCertifications,
    handleExportCSV,
    ensureSpacesAndFacilitators,
    ensureFacilitators,
  } = useAdminData()

  // Check authentication on mount
  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin/login')
      return
    }
    
    const cachedAdmin = getAdmin()
    setAdmin(cachedAdmin)
    
    // Load initial stats
    fetchStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate])

  const handleLogout = () => {
    clearAdminAuth()
    navigate('/admin/login')
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    loadTabData(tab)
  }

  const handleExportCohortCSV = () => {
    handleExportCSV('build3-cohort-7', 'build3-cohort-7-session-data.csv')
  }

  const handleExportMarchCohortCSV = () => {
    handleExportCSV('energizers-march-cohort', 'energizers-march-cohort-session-data.csv')
  }

  const handleExportMidDayResetCSV = () => {
    handleExportCSV('mid-day-reset', 'mid-day-reset-session-data.csv')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <AdminHeader admin={admin} onLogout={handleLogout} />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <AdminTabNav activeTab={activeTab} onTabChange={handleTabChange} />

          {/* Tab Content */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
            {loadingTab ? (
              <div className="flex justify-center items-center py-16">
                <LoadingSpinner />
              </div>
            ) : tabError ? (
              <div className="text-red-400 text-center py-16 bg-red-500/10 rounded-xl">
                {tabError}
              </div>
            ) : (
              <>
                {activeTab === 'stats' && <StatsTab stats={data.stats} />}
                {activeTab === 'feedback' && <FeedbackTab feedback={data.feedback} />}
                {activeTab === 'build3' && <Build3Tab build3={data.build3} />}
                {activeTab === 'wellness' && <WellnessTab wellness={data.wellness} />}
                {activeTab === 'cohort' && (
                  <CohortTab
                    cohort={data.cohort}
                    cohortStats={data.cohortStats}
                    cohortSessions={data.cohortSessions}
                    cohortCheckIns={data.cohortCheckIns}
                    cohortFeedback={data.cohortFeedback}
                    exportingCSV={exportingCSV}
                    onExportCSV={handleExportCohortCSV}
                    onExportMarchCSV={handleExportMarchCohortCSV}
                  />
                )}
                {activeTab === 'midDayReset' && (
                  <MidDayResetTab
                    midDayReset={data.midDayReset}
                    exportingCSV={exportingCSV}
                    onExportCSV={handleExportMidDayResetCSV}
                  />
                )}
                {activeTab === 'spaces' && (
                  <SpacesTab
                    spaces={data.spaces}
                    facilitators={data.facilitators}
                    onRefresh={fetchSpaces}
                    onEnsureFacilitators={ensureFacilitators}
                  />
                )}
                {activeTab === 'events' && (
                  <EventsTab
                    events={data.events}
                    spaces={data.spaces}
                    facilitators={data.facilitators}
                    onRefresh={fetchEvents}
                    onEnsureSpacesAndFacilitators={ensureSpacesAndFacilitators}
                  />
                )}
                {activeTab === 'oneOnOne' && <OneOnOneTab oneOnOneRequests={data.oneOnOneRequests} />}
                {activeTab === 'consultations' && (
                  <ConsultationsTab
                    consultations={data.consultations}
                    onRefresh={() => fetchConsultations(true)}
                  />
                )}
                {activeTab === 'facConsultations' && (
                  <FacilitatorConsultationsTab
                    listings={data.facConsultationListings}
                    interests={data.facConsultationInterests}
                    facilitators={data.facilitators}
                    onRefresh={() => fetchFacConsultations(true)}
                  />
                )}
                {activeTab === 'certifications' && (
                  <CertificationsTab
                    certifications={data.certifications}
                    onRefresh={() => fetchCertifications(true)}
                  />
                )}
              </>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  )
}

export default AdminDashboard
